import { useTu } from "@src/appV2/i18n/useTu";
import { Box, List, Stack } from "@mui/material";
import { SelectableListItem } from "@src/appV2/redesign/components/SelectableListItem/SelectableListItem";
import { Text } from "@src/appV2/redesign/components/Text";
import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";
import { APPLY_TO_OPTIONS, type ApplyToOption } from "./types";

interface ApplyToOptionsProps {
  selectedOption: ApplyToOption;
  onOptionChange: (option: ApplyToOption) => void;
  showAllShiftsOption?: boolean;
  timeSlot: TimeSlotWithExtras;
  qualificationName?: string;
}

interface OptionConfig {
  value: ApplyToOption;
  label: string;
  description: string;
}

function getTimeSlotLabel(timeSlot: TimeSlotWithExtras): string {
  return timeSlot.toUpperCase();
}

function getOptions(
  timeSlot: TimeSlotWithExtras,
  tu: (defaultText: string, options?: { values?: Record<string, string> }) => string,
  qualificationName?: string
): OptionConfig[] {
  const slotLabel = getTimeSlotLabel(timeSlot);
  const qualificationPrefix = qualificationName ? `${qualificationName} ` : "";

  return [
    {
      value: APPLY_TO_OPTIONS.THIS_SHIFT_ONLY,
      label: tu("Just to this shift"),
      description: tu("Set these times just for this shift"),
    },
    {
      value: APPLY_TO_OPTIONS.THIS_AND_FOLLOWING,
      label: tu("Only to {{shiftGroup}} shifts", {
        values: { shiftGroup: `${qualificationPrefix}${slotLabel}` },
      }),
      description: tu("Will only be applied to {{shiftGroup}} shifts", {
        values: { shiftGroup: `${qualificationPrefix}${slotLabel}` },
      }),
    },
    {
      value: APPLY_TO_OPTIONS.ALL_SHIFTS,
      label: tu("Apply to all {{slotLabel}} shifts", { values: { slotLabel } }),
      description: tu("Set these times as default for all {{slotLabel}} shifts", {
        values: { slotLabel },
      }),
    },
  ];
}

export function ApplyToOptions(props: ApplyToOptionsProps) {
  const { tu } = useTu("schedule");

  const {
    selectedOption,
    onOptionChange,
    showAllShiftsOption = true,
    timeSlot,
    qualificationName,
  } = props;

  const isOrientation = timeSlot === "ori";
  const options = getOptions(timeSlot, tu, qualificationName);

  const visibleOptions = options.filter((option) => {
    if (!showAllShiftsOption && option.value === APPLY_TO_OPTIONS.ALL_SHIFTS) {
      return false;
    }
    if (isOrientation && option.value === APPLY_TO_OPTIONS.THIS_AND_FOLLOWING) {
      return false;
    }
    return true;
  });

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.borderRadius?.xLarge,
        overflow: "hidden",
      })}
    >
      <Box
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.border?.base}`,
          padding: theme.spacing(4),
          display: "flex",
          justifyContent: "center",
        })}
      >
        <Text color="text.secondary" variant="caption">
          {tu("How do you want to apply this time change?")}
        </Text>
      </Box>
      <List disablePadding>
        {visibleOptions.map((option, index) => (
          <Box
            key={option.value}
            sx={(theme) => ({
              borderBottom:
                index < visibleOptions.length - 1
                  ? `1px solid ${theme.palette.border?.base}`
                  : "none",
            })}
          >
            <SelectableListItem
              isSelected={selectedOption === option.value}
              selectionMode="radio"
              onSelect={() => {
                onOptionChange(option.value);
              }}
            >
              <Stack spacing={0.5}>
                <Text semibold>{option.label}</Text>
                <Text color="text.secondary" variant="caption">
                  {option.description}
                </Text>
              </Stack>
            </SelectableListItem>
          </Box>
        ))}
      </List>
    </Box>
  );
}
